<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("page.category_manager.start_over") }}</template>

        <template v-slot:body>
            <div v-if="!initDone" class="columns is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
            <div v-else class="columns">
                <div class="column">
                    <Notification v-if="isStartOverPossible" class="is-danger" :centered="false">
                        <span class="icon"><i class="fas fa-exclamation-triangle" /></span>
                        {{ translate("page.category_manager.start_over_warning", false, [store.totalCategoryCount]) }}
                    </Notification>

                    <Notification v-else class="is-warning" :centered="false">
                        <span class="icon"><i class="fas fa-lock" /></span>
                        {{ translate("page.category_manager.start_over_blocked") }}
                    </Notification>
                </div>
            </div>
        </template>

        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <ConfirmButton
                    v-if="isStartOverPossible"
                    ref="confirmButton"
                    :disabled="deletingInProgress"
                    first-class="button is-danger"
                    second-class="button is-danger"
                    @click="startOver"
                >
                    <template v-slot:firstClick>
                        <span class="icon"><i class="fa fa-trash-alt" /></span>
                        <span>{{ translate("page.category_manager.start_over_confirm_button") }}</span>
                    </template>
                    <template v-slot:secondClick>
                        <span class="icon"><i class="fa fa-exclamation-triangle" /></span>
                        <span>{{ translate("common.are_you_sure") }}</span>
                    </template>
                </ConfirmButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import categoriesApi, { ParameterLeafStatus } from "../Api/Categories/categories";
    import { handleError } from "../Common/errorHandler";
    import { translate } from "../Common/translations";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import ConfirmButton from "../Common/ConfirmButton.vue";
    import Notification from "../Common/Notification.vue";
    import { successToast } from "../Common/toasts";
    import { useCategoriesManagerStore } from "./categoriesManagerStore";
    import { useModal } from "../Common/useModal";

    const store = useCategoriesManagerStore();
    const { open: openModal, close, modal } = useModal();

    const categories = ref<ParameterLeafStatus[]>([]);
    const initDone = ref(false);
    const deletingInProgress = ref(false);
    const confirmButton = ref<InstanceType<typeof ConfirmButton> | null>(null);

    const isStartOverPossible = computed(() => categories.value.every(category => category.totalRegistrations === 0));

    function open() {
        initDone.value = false;
        categories.value = [];
        confirmButton.value?.reset();
        openModal();

        if (!store.rootNode) {
            initDone.value = true;
            return Promise.resolve();
        }

        return categoriesApi
            .getTreeStatusForParameter(store.eventId, store.rootNode.id)
            .then(categoriesResponse => (categories.value = categoriesResponse))
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    function startOver() {
        if (!store.rootNode) {
            return;
        }

        deletingInProgress.value = true;
        return categoriesApi
            .deleteParameterTree(store.eventId, store.rootNode.id)
            .then(() => store.refreshCategories())
            .then(() => {
                close();
                successToast(translate("page.category_manager.started_over_toast"));
            })
            .catch(error => {
                confirmButton.value?.reset();
                handleError(error);
            })
            .finally(() => {
                deletingInProgress.value = false;
            });
    }

    defineExpose({
        open,
    });
</script>

<style scoped></style>
